// src/pages/driverReports/carSheets/buildSheets/runningExpensesUtils.js
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../firebase/firebase";
import { finalizeSheetTotals } from "../helpers";
import { normalizePlateDigits } from "../../../earningsImport/runningExpensesParser";

const MANAGED_CATEGORIES = ["SALIK", "DARB", "PETROL", "PARKING", "PARKONIC", "RTA PARKING"];

function norm(value) {
  return String(value || "").trim().toUpperCase().replace(/\s+/g, " ");
}

function startOfDayMs(value) {
  if (!value) return 0;
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (!d || Number.isNaN(d.getTime())) return 0;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function endOfDayMs(value) {
  if (!value) return 0;
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (!d || Number.isNaN(d.getTime())) return 0;
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function isDateInSheet(expenseDate, sheet) {
  const t = startOfDayMs(expenseDate);
  const s = startOfDayMs(sheet?.periodStart);
  const e = endOfDayMs(sheet?.periodEnd);
  return Boolean(t && s && e && t >= s && t <= e);
}

function isAutoEligibleSheet(sheet) {
  if (!sheet) return false;
  if (sheet.isCarryForwardOnly) return false;
  if (sheet.isManualDriverSheet) return false;
  if (String(sheet.carNumber || "").toUpperCase().startsWith("MANUAL-")) return false;
  return true;
}

function findSheetForExpense(sheets, expense) {
  const plateDigits = normalizePlateDigits(expense.plateDigits || expense.plate || expense.vehicleNumber);
  if (!plateDigits) return null;

  const candidates = (sheets || []).filter((sheet) => {
    if (!isAutoEligibleSheet(sheet)) return false;
    const sheetDigits = normalizePlateDigits(sheet.carNumber || sheet.vehicleNumber);
    return sheetDigits === plateDigits && isDateInSheet(expense.tripDate || expense.date, sheet);
  });

  if (!candidates.length) return null;

  // Prefer the smallest matching period if overlapping sheets exist.
  return candidates.sort((a, b) => {
    const daysA = endOfDayMs(a.periodEnd) - startOfDayMs(a.periodStart);
    const daysB = endOfDayMs(b.periodEnd) - startOfDayMs(b.periodStart);
    return daysA - daysB;
  })[0];
}

function ensureExpenseRow(sheet, category) {
  if (!Array.isArray(sheet.expensesRows)) sheet.expensesRows = [];

  const key = norm(category);
  let row = sheet.expensesRows.find((r) => norm(r.label) === key);

  if (!row) {
    row = {
      id: `exp_${key.toLowerCase().replace(/\s+/g, "_")}`,
      label: key,
      col1: "",
      col2: "",
      col3: "",
      total: 0,
    };
    const totalIndex = sheet.expensesRows.findIndex((r) => r.isSummary);
    if (totalIndex >= 0) sheet.expensesRows.splice(totalIndex, 0, row);
    else sheet.expensesRows.push(row);
  }

  return row;
}

function clearOldAutoRunningRows(sheet) {
  if (!Array.isArray(sheet.expensesRows)) return;

  sheet.expensesRows.forEach((row) => {
    if (!row?.autoRunningExpense) return;
    if (!MANAGED_CATEGORIES.includes(norm(row.label))) return;

    row.total = 0;
    row.description = "";
    row.col1 = "";
    row.col2 = "";
    row.col3 = "";
    row.autoRunningExpense = false;
    row.runningExpenseCategory = "";
    row.runningExpenseCount = 0;
  });
}

export async function loadRunningExpensesForMonth(month) {
  if (!month) return [];

  const snap = await getDocs(collection(db, "carRunningExpenses", month, "rows"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export function previewRunningExpenseMatches({ sheets = [], expenses = [] }) {
  const matched = [];
  const unmatched = [];

  expenses.forEach((expense) => {
    const sheet = findSheetForExpense(sheets, expense);
    if (sheet) {
      matched.push({ ...expense, sheetId: sheet.id, carNumber: sheet.carNumber });
    } else {
      unmatched.push(expense);
    }
  });

  return { matched, unmatched };
}

export function applyRunningExpensesToSheets({ sheets = [], expenses = [] }) {
  const sheetList = Array.isArray(sheets) ? sheets : [];
  const expenseList = Array.isArray(expenses) ? expenses : [];

  sheetList.forEach(clearOldAutoRunningRows);

  const groups = new Map();
  const unmatched = [];

  expenseList.forEach((expense) => {
    const category = norm(expense.category || expense.expenseType);
    if (!MANAGED_CATEGORIES.includes(category)) return;

    const sheet = findSheetForExpense(sheetList, expense);
    if (!sheet) {
      unmatched.push(expense);
      return;
    }

    const key = `${sheet.id}__${category}`;
    if (!groups.has(key)) {
      groups.set(key, {
        sheet,
        category,
        total: 0,
        count: 0,
        firstDate: expense.tripDate || expense.date || "",
        lastDate: expense.tripDate || expense.date || "",
      });
    }

    const group = groups.get(key);
    const amt = Number(expense.amount || 0);
    group.total += Number.isFinite(amt) ? amt : 0;
    group.count += 1;

    const d = String(expense.tripDate || expense.date || "");
    if (d && (!group.firstDate || d < group.firstDate)) group.firstDate = d;
    if (d && (!group.lastDate || d > group.lastDate)) group.lastDate = d;
  });

  groups.forEach((group) => {
    const row = ensureExpenseRow(group.sheet, group.category);

    row.total = Number(group.total.toFixed(2));
    // Keep the statement clean: show only the final amount in the total column.
    // Do not show AUTO / transaction count / date range in the visible middle columns.
    row.description = "";
    row.col1 = "";
    row.col2 = "";
    row.col3 = "";
    row.autoDetails = `Auto ${group.category}: ${group.count} transaction(s), ${group.firstDate} to ${group.lastDate}`;
    row.autoRunningExpense = true;
    row.runningExpenseCategory = group.category;
    row.runningExpenseCount = group.count;
  });

  sheetList.forEach((sheet) => {
    try {
      finalizeSheetTotals(sheet);
    } catch (err) {
      console.warn("Could not finalize sheet after running expenses:", sheet?.id, err);
    }
  });

  return {
    appliedGroups: groups.size,
    unmatched,
  };
}
